import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ClipboardText, ClockClockwise, MagnifyingGlass, Plus } from 'phosphor-react';
import { IData, IPagination } from '../../../types/shared';
import { PageBox } from '../../../components/pageBox';
import { Paginator } from '../../../components/paginator';
import { useDialog } from '../../../providers/dialog';
import { useAuth } from '../../../providers/auth';
import { IContratoSocietario, useSocietario } from '../../../hooks/useSocietario';
import { ContratoSocietarioCard } from './components/ContratoSocietarioCard';
import { AbasDaListagem } from './components/AbasDaListagem';
import { FiltrosDaListagem } from './components/FiltrosDaListagem';
import { extractApiErrorMessage } from '../../../utils/ErrorUtils';
import {
  AbaDaListagem,
  ABAS_DA_LISTAGEM,
  ORDEM_PADRAO,
  OPCOES_DE_ORDEM,
  OrdemDaListagem,
  statusCabeNaAba,
} from './listagem';

export const SocietarioContratosPage = () => {
  const queryClient = useQueryClient();
  const {
    getContratos,
    getContagemContratos,
    getCriadoresContratos,
    getContrato,
    downloadContrato,
    downloadContratoVigente,
    uploadDocumentoEditado,
    arquivarContrato,
  } = useSocietario();
  const showDialog = useDialog();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const userId: number | undefined = user?.id;

  const [filters, setFilters] = useSearchParams(
    createSearchParams({
      page: '1',
      size: '20',
    }),
  );

  // Sessao 2026-05-08 (Fase D.5): aba ativa lida da URL (parametro `aba`).
  // Cada aba aplica um filtro pre-definido sem perder os filtros manuais.
  const abaParam = (filters.get('aba') as AbaDaListagem | null) ?? 'todos';
  const abaAtiva: AbaDaListagem = ABAS_DA_LISTAGEM.includes(abaParam) ? abaParam : 'todos';

  const busca = filters.get('search') ?? '';
  const tipoFiltro = filters.get('tipo_operacao') ?? '';
  // Status que nao cabe na aba (link antigo, troca de aba) fica de fora da consulta.
  const statusDaUrl = filters.get('status');
  const statusFiltro = statusDaUrl && statusCabeNaAba(statusDaUrl, abaAtiva) ? statusDaUrl : '';
  const ordemDaUrl = filters.get('ordenar') as OrdemDaListagem | null;
  // Sem ordem na URL, os atualizados primeiro: o back, sem `ordenar`, devolve por
  // id crescente e o contrato do dia ia parar na ultima pagina.
  const ordem: OrdemDaListagem =
    ordemDaUrl && OPCOES_DE_ORDEM.some(o => o.value === ordemDaUrl) ? ordemDaUrl : ORDEM_PADRAO;
  const empresaIdFiltro = filters.get('empresa_id');
  // Quem criou (29/09). Na aba Minhas não vale: lá já são os do usuário.
  const criadoPorDaUrl = filters.get('criado_por') ?? '';
  const criadoPorFiltro = abaAtiva === 'minhas' ? '' : criadoPorDaUrl;

  const queryFilters = useMemo(() => {
    const params: Record<string, any> = {
      page: Number(filters.get('page') ?? '1'),
      size: Number(filters.get('size') ?? '20'),
      ordenar: ordem,
    };
    if (tipoFiltro) params.tipo_operacao = tipoFiltro;
    if (statusFiltro) params.status = statusFiltro;
    if (busca) params.search = busca;
    if (empresaIdFiltro) params.empresa_id = Number(empresaIdFiltro);
    if (criadoPorFiltro) params.criado_por = Number(criadoPorFiltro);

    // Aplica filtros pre-definidos da aba ativa.
    if (abaAtiva === 'minhas' && userId != null) {
      // "Minhas solicitacoes" — TODAS as solicitacoes do usuario, independente
      // de status. Substitui a antiga pagina /societario/historico, que listava
      // todos os contratos sem filtro pessoal. Aqui restringimos a `criado_por`
      // pra cada usuario ver so o que ele iniciou — auditoria pessoal.
      params.criado_por = userId;
    } else if (abaAtiva === 'finalizados') {
      // Backend aceita CSV em `?status=` — pega gerado e assinado juntos.
      if (!params.status) params.status = 'gerado,assinado';
    } else if (abaAtiva === 'arquivados') {
      params.status = 'arquivado';
      params.incluir_arquivados = true;
    }
    return params;
  }, [
    filters,
    abaAtiva,
    userId,
    ordem,
    tipoFiltro,
    statusFiltro,
    busca,
    empresaIdFiltro,
    criadoPorFiltro,
  ]);

  // Total de cada aba com os mesmos tipo e busca (o status nao entra: as abas sao
  // recortes de status).
  const contagemFilters = useMemo(() => {
    const params: Record<string, any> = {};
    if (tipoFiltro) params.tipo_operacao = tipoFiltro;
    if (busca) params.search = busca;
    if (empresaIdFiltro) params.empresa_id = Number(empresaIdFiltro);
    if (criadoPorFiltro) params.criado_por = Number(criadoPorFiltro);
    return params;
  }, [tipoFiltro, busca, empresaIdFiltro, criadoPorFiltro]);
  const { data: contagemData } = useQuery({
    // Debaixo de 'societario-contratos': quem invalida a lista (card, WS, ações)
    // atualiza junto os números das abas e o "Criado por".
    queryKey: ['societario-contratos', 'contagem', contagemFilters],
    queryFn: getContagemContratos,
    placeholderData: keepPreviousData,
  });
  const contagem = contagemData?.data;

  // Opções do "Criado por": quem tem contrato ativo, com o mesmo tipo e busca.
  const criadoresFilters = useMemo(() => {
    const params: Record<string, any> = {};
    if (tipoFiltro) params.tipo_operacao = tipoFiltro;
    if (busca) params.search = busca;
    if (empresaIdFiltro) params.empresa_id = Number(empresaIdFiltro);
    return params;
  }, [tipoFiltro, busca, empresaIdFiltro]);
  const { data: criadoresData } = useQuery({
    queryKey: ['societario-contratos', 'criadores', criadoresFilters],
    queryFn: getCriadoresContratos,
    placeholderData: keepPreviousData,
  });

  const { data, isFetching, isPlaceholderData } = useQuery<IData<IContratoSocietario[]>>({
    queryKey: ['societario-contratos', queryFilters],
    queryFn: getContratos,
    // A cada busca/filtro a chave muda: sem isto a grade some e o spinner pisca a
    // cada pausa na digitacao. A lista anterior fica, esmaecida, ate' a nova chegar.
    placeholderData: keepPreviousData,
    // Auto-refetch enquanto houver contrato em geracao v2 async
    // (`task_id_em_andamento` populado). Em modo debug o broadcaster Redis
    // do WS fica desabilitado, entao sem este polling o usuario nao ve a
    // conclusao quando volta para a listagem. Em producao (com WS funcional)
    // o intervalo continua sendo um fallback inofensivo. Slice 6-D vai
    // substituir isso por um modal dedicado consumindo subscribeGeracaoStatus.
    refetchInterval: query => {
      const items = query.state.data?.data?.items ?? [];
      const temGeracaoEmAndamento = items.some(c => !!c?.task_id_em_andamento);
      return temGeracaoEmAndamento ? 5000 : false;
    },
  });

  const pageData = data?.data;
  const items = pageData?.items ?? [];
  const pagination: IPagination = {
    page: pageData?.page ?? 1,
    pages: pageData?.pages ?? 1,
    size: pageData?.size ?? 20,
    total: pageData?.total ?? 0,
    next_page: pageData?.next_page ?? null,
    previous_page: pageData?.previous_page ?? null,
  };

  const { mutateAsync: arquivarContratoMut } = useMutation({
    mutationFn: arquivarContrato,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['societario-contratos'] });
      toast({ status: 'success', title: 'Contrato arquivado' });
    },
    onError: () => {
      toast({ status: 'error', title: 'Falha ao arquivar contrato' });
    },
  });

  // ⚠️ NAO usar window.open aqui. A URL só existe DEPOIS do await, e nesse
  // ponto a ativação transitória do clique já foi consumida — com o menu
  // fechando e devolvendo o foco, o Chrome bloqueia o popup em silêncio: a API
  // responde 200, nenhuma aba abre, nenhum erro aparece. Medido no #136.
  //
  // A URL assinada já vem com `Content-Disposition: attachment`
  // (`get_societario_url(..., filename=...)`), então atribuir ao location
  // BAIXA sem sair da página — e sem depender de popup.
  const baixarArquivo = (url: string) => {
    window.location.href = url;
  };

  const handleDownload = async (id: number, opcoes?: { original?: boolean }) => {
    try {
      const res = await downloadContrato(id, opcoes);
      const url = res.data?.url;
      if (url) {
        baixarArquivo(url);
        // ⚠️ Download com `Content-Disposition: attachment` é MUDO: não abre
        // aba, não muda a página, o menu só fecha. Sem este aviso o operador
        // clica e conclui que não funcionou — foi exatamente o que aconteceu
        // (o arquivo estava na pasta de Downloads o tempo todo). O toast também
        // diz QUAL versão veio, que é a dúvida real quando existem duas.
        toast({
          status: 'success',
          title:
            res.data?.versao === 'revisada'
              ? 'Baixando a versão revisada'
              : 'Baixando o contrato gerado',
          description: 'Confira a pasta de downloads do navegador.',
          duration: 4000,
        });
      } else {
        toast({ status: 'warning', title: 'URL de download indisponível' });
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      // Gate de fidelidade (Fase 1): o back barra o download da alteração v3
      // com 409 AUDITORIA_NAO_REVISADA enquanto a vistoria não foi confirmada.
      // Só chega aqui com a lista defasada (contrato regerado por outro usuário
      // / lag do serializer) — o card já esconde o botão quando `auditoriaPendente`.
      // Em vez do erro genérico cru, orienta o operador à vistoria (lupa no card)
      // e revalida a lista pra o card se corrigir (esconder download + mostrar lupa).
      if (typeof detail === 'string' && detail.startsWith('AUDITORIA_NAO_REVISADA')) {
        queryClient.invalidateQueries({ queryKey: ['societario-contratos'] });
        toast({
          status: 'info',
          title: 'Revise a vistoria de fidelidade',
          description:
            'Abra a lupa no card para conferir a vistoria (vigente × render) e liberar o download.',
        });
        return;
      }
      toast({
        status: 'error',
        title: 'Falha ao baixar contrato',
        description: extractApiErrorMessage(err),
      });
    }
  };

  const handleEnviarRevisado = async (id: number, file: File) => {
    try {
      await uploadDocumentoEditado({ contratoId: id, file });
      // Revalida: o card precisa passar a mostrar o badge "revisado" e o menu
      // de duas opções no download.
      queryClient.invalidateQueries({ queryKey: ['societario-contratos'] });
      toast({
        status: 'success',
        title: 'Versão revisada anexada',
        description:
          'Ela passa a ser o documento deste ato. O original gerado continua ' +
          'disponível no menu de download.',
      });
    } catch (err: any) {
      toast({
        status: 'error',
        title: 'Falha ao enviar a versão revisada',
        description: extractApiErrorMessage(err),
      });
    }
  };

  const handleDownloadVigente = async (id: number) => {
    try {
      const res = await downloadContratoVigente(id);
      const url = res.data?.url;
      if (url) {
        baixarArquivo(url);
        toast({
          status: 'success',
          title: 'Baixando o contrato vigente',
          description: 'Confira a pasta de downloads do navegador.',
          duration: 4000,
        });
      } else {
        toast({ status: 'warning', title: 'URL do vigente indisponível' });
      }
    } catch (err: any) {
      // 404 = lista defasada (vigente removido / contrato sem anexo). Revalida
      // pra o card se corrigir (esconder o botão) e orienta o operador.
      if (err?.response?.status === 404) {
        queryClient.invalidateQueries({ queryKey: ['societario-contratos'] });
        toast({ status: 'info', title: 'Nenhum contrato vigente anexado a este contrato' });
        return;
      }
      toast({
        status: 'error',
        title: 'Falha ao baixar contrato vigente',
        description: extractApiErrorMessage(err),
      });
    }
  };

  const updateFilter = useCallback(
    (key: string, value: string) => {
      setFilters(state => {
        if (value) {
          state.set(key, value);
        } else {
          state.delete(key);
        }
        state.set('page', '1');
        return state;
      });
    },
    [setFilters],
  );

  const temFiltro = !!(
    busca ||
    tipoFiltro ||
    statusFiltro ||
    criadoPorFiltro ||
    ordem !== ORDEM_PADRAO
  );
  const limparFiltros = () => {
    setFilters(state => {
      ['search', 'tipo_operacao', 'status', 'ordenar', 'criado_por'].forEach(k => state.delete(k));
      state.set('page', '1');
      return state;
    });
  };

  const handleRemover = (contrato: IContratoSocietario) => {
    // Sessao 2026-05-08 (Fase D): a acao "Remover" no card agora ARQUIVA
    // (status='arquivado'), nao apaga. Operador pode consultar depois na
    // aba Arquivados. Pra exclusao logica permanente, usa o endpoint
    // DELETE direto (nao exposto na UI hoje).
    showDialog({
      status: 'warning',
      title: 'Arquivar contrato?',
      description: `Esta ação moverá o contrato #${contrato.id} para a aba Arquivados.`,
      cancelLabel: 'Cancelar',
      async onConfirm() {
        await arquivarContratoMut(contrato.id);
      },
    });
  };

  const handleTabChange = (aba: AbaDaListagem) => {
    setFilters(state => {
      if (aba === 'todos') {
        state.delete('aba');
      } else {
        state.set('aba', aba);
      }
      if (!statusCabeNaAba(state.get('status'), aba)) state.delete('status');
      // Minhas já é "criado por mim": um "Criado por" de outra pessoa ficaria escondido.
      if (aba === 'minhas') state.delete('criado_por');
      // Reset page ao trocar aba.
      state.set('page', '1');
      return state;
    });
  };

  // Deep-link `?destaque=<id>` (vem da legalização ao abrir um contrato já gerado): rola
  // até o card e o realça. Se o contrato não estiver na página/filtro atual, é no-op.
  const destaqueId = Number(filters.get('destaque')) || null;
  useEffect(() => {
    if (!destaqueId || items.length === 0) return;
    document
      .getElementById(`contrato-card-${destaqueId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [destaqueId, items.length]);

  // ⚠️ O deep-link `?destaque=` sozinho SO' rolava a tela ate' o card — e' no-op
  // quando o contrato nao esta' na pagina atual. Na pratica o operador clicava
  // na notificacao "contrato pronto", caia na listagem e o contrato estava na
  // 3a pagina: ele tinha que procurar na mao, que e' exatamente o que a
  // notificacao deveria poupar. Busca o contrato POR ID e mostra em destaque no
  // topo, independente de pagina e de filtro.
  const { data: contratoDestaque } = useQuery({
    queryKey: ['contrato-destaque', destaqueId],
    // padrao da casa: o hook le o id de queryKey[1] via QueryFunctionContext
    queryFn: getContrato,
    enabled: !!destaqueId,
    staleTime: 30_000,
  });

  // Se ele ja' aparece na pagina atual, nao duplica o card.
  const contratoDoDestaque = contratoDestaque?.data;
  const destaqueForaDaPagina = !!contratoDoDestaque && !items.some(c => c.id === destaqueId);

  const renderDestaque = () => {
    if (!destaqueForaDaPagina || !contratoDoDestaque) return null;
    return (
      <Box mb={6}>
        <Flex align="center" justify="space-between" mb={2}>
          <Text fontSize="sm" fontWeight="600" color="gray.600">
            Contrato que você abriu pela notificação
          </Text>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => {
              const proximos = new URLSearchParams(filters);
              proximos.delete('destaque');
              setFilters(proximos);
            }}
          >
            Dispensar
          </Button>
        </Flex>
        <Box maxW={{ base: '100%', md: '380px' }}>
          <ContratoSocietarioCard
            contrato={contratoDoDestaque}
            destacado
            onDownload={handleDownload}
            onEnviarRevisado={handleEnviarRevisado}
            onDownloadVigente={handleDownloadVigente}
            onRemover={handleRemover}
            onContinuarEdicao={c => navigate(`criar?contratoId=${c.id}`)}
          />
        </Box>
      </Box>
    );
  };

  const renderList = () => {
    if (isFetching && items.length === 0) {
      return (
        <Center py={20}>
          <Spinner size="lg" color="blue.500" thickness="3px" />
        </Center>
      );
    }
    if (items.length === 0) {
      return (
        <Center py={20} flexDirection="column" gap={3}>
          <Text color="gray.500">
            {temFiltro
              ? 'Nenhum contrato encontrado com esses filtros.'
              : 'Nenhum contrato encontrado.'}
          </Text>
          {temFiltro && (
            <Button variant="outline" size="sm" onClick={limparFiltros}>
              Limpar filtros
            </Button>
          )}
        </Center>
      );
    }
    return (
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3 }}
        spacing={4}
        opacity={isPlaceholderData ? 0.55 : 1}
        transition="opacity 0.2s"
        aria-busy={isPlaceholderData}
      >
        {items.map(contrato => (
          <Box key={contrato.id} id={`contrato-card-${contrato.id}`}>
            <ContratoSocietarioCard
              contrato={contrato}
              destacado={contrato.id === destaqueId}
              onDownload={handleDownload}
              onEnviarRevisado={handleEnviarRevisado}
              onDownloadVigente={handleDownloadVigente}
              onRemover={handleRemover}
              onContinuarEdicao={c => navigate(`criar?contratoId=${c.id}`)}
            />
          </Box>
        ))}
      </SimpleGrid>
    );
  };

  // Concluidos = finalizados (gerado/assinado) menos os que esperam a vistoria.
  const concluidos = contagem ? Math.max(contagem.finalizados - contagem.vistoria_pendente, 0) : 0;
  const totalDaBusca = pagination.total;

  return (
    <PageBox>
      <Box minH="100vh" bg="gray.50">
        {/* Cabecalho no padrao dos outros submodulos (Estatutos, Legalizacao): faixa
            branca, icone do modulo, indicadores e a acao principal; embaixo, as abas
            segmentadas e a busca. */}
        <Box
          bg="white"
          borderBottomWidth="1px"
          borderColor="gray.100"
          px={{ base: 4, md: 8 }}
          py={5}
        >
          <Flex
            align={{ base: 'flex-start', md: 'center' }}
            justify="space-between"
            gap={4}
            flexDir={{ base: 'column', md: 'row' }}
            mb={4}
          >
            <Flex align="center" gap={3}>
              {/* Mesmo icone e cor do card de Contratos na home do societario. */}
              <Box
                w="48px"
                h="48px"
                borderRadius="xl"
                bg="#DBEAFE"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <ClipboardText size={26} color="#1976D2" weight="duotone" />
              </Box>
              <Box>
                <Text
                  fontSize="10px"
                  color="gray.400"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="0.1em"
                >
                  Módulo
                </Text>
                <Heading
                  fontSize={{ base: 'lg', md: 'xl' }}
                  fontWeight="700"
                  color="gray.900"
                  letterSpacing="-0.02em"
                >
                  Contratos Societários
                </Heading>
                {/* No lugar do subtitulo, os numeros do que pede atencao (os irmaos
                    mostram a contagem aqui). Sem contagem ainda, a descricao. */}
                {contagem ? (
                  <Flex gap={3} mt={0.5} fontSize="xs" color="gray.500" wrap="wrap">
                    {[
                      {
                        valor: contagem.em_andamento,
                        rotulo: 'em andamento',
                        cor: '#1B88F5',
                        Icone: ClockClockwise,
                      },
                      {
                        valor: contagem.vistoria_pendente,
                        rotulo: 'aguardando vistoria',
                        cor: '#F59E0B',
                        Icone: MagnifyingGlass,
                      },
                      {
                        valor: concluidos,
                        rotulo: 'concluídos',
                        cor: '#10B981',
                        Icone: CheckCircle,
                      },
                    ].map(({ valor, rotulo, cor, Icone }) => (
                      <Flex key={rotulo} align="center" gap={1}>
                        <Icone size={13} color={cor} weight="bold" />
                        <Text as="span" fontWeight="700" color="gray.700">
                          {valor}
                        </Text>
                        <Text as="span">{rotulo}</Text>
                      </Flex>
                    ))}
                  </Flex>
                ) : (
                  <Text fontSize="xs" color="gray.500" mt={0.5}>
                    Constituição e alteração contratual
                  </Text>
                )}
              </Box>
            </Flex>

            <Button leftIcon={<Plus size={16} weight="bold" />} onClick={() => navigate('criar')}>
              Novo contrato
            </Button>
          </Flex>

          <AbasDaListagem aba={abaAtiva} onTrocar={handleTabChange} contagem={contagem} />
          {/* Busca e filtros juntos, numa linha própria (como na Legalização): ao lado das
              abas, o grupo de filtros caía sozinho na linha de baixo. */}
          <Flex gap={3} flexWrap="wrap" align="center" mt={3}>
            <FiltrosDaListagem
              aba={abaAtiva}
              busca={busca}
              onBusca={valor => updateFilter('search', valor)}
              tipo={tipoFiltro}
              onTipo={valor => updateFilter('tipo_operacao', valor)}
              status={statusFiltro}
              onStatus={valor => updateFilter('status', valor)}
              ordem={ordem}
              onOrdem={valor => updateFilter('ordenar', valor === ORDEM_PADRAO ? '' : valor)}
              criadoPor={criadoPorFiltro}
              onCriadoPor={valor => updateFilter('criado_por', valor)}
              criadores={criadoresData?.data ?? []}
            />
          </Flex>
        </Box>

        <Box px={{ base: 4, md: 8 }} py={6}>
          {/* Com busca ou filtro, diz quanto achou e como desfazer (sem resultado, o
              aviso da lista vazia ja' oferece o Limpar). */}
          {temFiltro && !(isFetching && !data) && totalDaBusca > 0 && (
            <Flex justify="space-between" align="center" mb={4} gap={3} minH="28px">
              <Text fontSize="sm" color="gray.600" aria-live="polite">
                {totalDaBusca} {totalDaBusca === 1 ? 'contrato' : 'contratos'}
                {busca ? ` para "${busca}"` : ' com esses filtros'}
              </Text>
              <Button
                variant="ghost"
                size="sm"
                h="28px"
                px={2}
                fontSize="sm"
                color="blue.600"
                onClick={limparFiltros}
              >
                Limpar filtros
              </Button>
            </Flex>
          )}

          {renderDestaque()}

          {renderList()}

          {/* Numa página vazia além do fim (ex.: arquivou o último da página 2), o
              paginador fica para o operador voltar. */}
          {(pagination.pages > 1 || pagination.page > 1) && (
            <Box mt={6}>
              <Paginator pagination={pagination} />
            </Box>
          )}
        </Box>
      </Box>
    </PageBox>
  );
};

export default SocietarioContratosPage;
